/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../nls/StringResources',
	'react',
	'prop-types',
	'@ba/ba-ui-carbon',
	'@ba/ba-ui-carbon-toolkit',
	'./DataSourcePanelPage',
	'../DataSourcePanelColumnInsertionHelper',
	'../DataSourcePanelStatic',
	'../../../utils/ShapingUIUtils',
	'../../../../lib/@waca/core-client/js/core-client/ui/KeyCodes'
], function (
	_,
	StringResources,
	React,
	PropTypes,
	CarbonToolkit,
	Toolkit,
	DataSourcePanelPage,
	DataSourcePanelColumnInsertionHelper,
	DataSourcePanelStatic,
	ShapingUIUtils,
	KeyCodes
) {

	const { InlineLoading } = CarbonToolkit;
	const { FlexLayout, Label } = Toolkit;

	const MODULE_STATUS = {
		PENDING: 'pending',
		FULFILLED: 'fulfilled',
		REJECTED: 'rejected'
	};

	const GLASS_COLLECTION__BUTTONS = 'com.ibm.bi.dashboard.dataSourcePanel.metadata.buttons';

	class RefPromise {
		constructor() {
			this.ref = React.createRef();
			this.promise = new Promise(resolve => this._set = resolve);
			this.promise.then(() => this._set = null);
		}
		update() {
			if(this._set && this.ref.current) {
				this._set(this.ref.current);
			}
		}
	}

	class DataSourcePanelMetadataTree extends DataSourcePanelPage {

		static propTypes = {
			dashboardApi: PropTypes.object.isRequired,
			dataSource: PropTypes.object.isRequired,
			moserDatasources: PropTypes.object,
			onShowDataSourceList: PropTypes.func.isRequired
		};

		static MODULE_STATUS = MODULE_STATUS;

		constructor(props) {
			super(props);

			this.state.moduleStatus = MODULE_STATUS.PENDING;

			this.treeContainer = new RefPromise();

			this._registerProgressHandler();
			this._initShapingTree();
			this._initState = Promise.all([
				this._initialize(),
				this._setupModuleStatus()
			]);
		}

		_registerProgressHandler() {
			this.props.moserDatasources && this.props.moserDatasources.registerLoadMetadataProgressHandler(
				this.props.dataSource.getId(),
				this._updateLoadPercentage.bind(this)
			);
		}

		_updateLoadPercentage(progressInfo) {
			this.setState({
				loadingPercentage: progressInfo.percentage
			});
		}

		_initShapingTree() {
			Promise.all([
				this.props.dataSource.getModule(),
				this.treeContainer.promise
			]).then(
				([module, element]) => this._renderShapingTree(module, element)
			);
		}

		_initialize() {
			this._insertionHelper = new DataSourcePanelColumnInsertionHelper({ dashboardApi: this.props.dashboardApi });
			return this.props.dataSource.getLocalizedName().then(
				name => this.setState({ name })
			);
		}

		_setupModuleStatus() {
			return this.props.dataSource.getModule().then(
				module => {
					if(this.props.dataSource.getState() === 'error') {
						this.setState({
							moduleStatus: MODULE_STATUS.REJECTED
						});
					} else {
						this.setState({
							moduleStatus: MODULE_STATUS.FULFILLED,
							module
						});
					}
				},
				() => {
					this.setState({
						moduleStatus: MODULE_STATUS.REJECTED
					});
				}
			);
		}

		getButtonCollectionId() {
			return GLASS_COLLECTION__BUTTONS;
		}

		getButtonConstructorPayload() {
			const payload = super.getButtonConstructorPayload();
			payload.dataSource = this.props.dataSource;
			return payload;
		}

		componentDidUpdate() {
			this.treeContainer.update();
		}

		componentDidMount() {
			this.treeContainer.update();
		}

		getTitleParams() {
			return {
				id: 'source',
				title: this.state.name
			};
		}

		renderPanel() {
			switch(this.state.moduleStatus) {
				case MODULE_STATUS.PENDING:
					return this._renderTreeLoading();
				case MODULE_STATUS.FULFILLED:
					return this._renderTreePlaceholder();
				default:
					return this._renderTreeError(
						StringResources.get('errorSourcesTitle'),
						StringResources.get('errorLoadingDataTree')
					);
			}
		}

		_getLoadProgressString() {
			if(this.state.loadingPercentage) {
				return StringResources.get('dataPreparationProgress', {
					percentage: this.state.loadingPercentage
				});
			}
		}

		_renderTreeLoading() {
			return (
				<FlexLayout
					alignItems='center'
					justifyContent='center'
					outerClassName='datasourcePanelLoading'
				>
					<div>
						<Label
							className='dataSourcePanelStaticTitle'
							label={StringResources.get('dataPreparationTitle')}
						/>
						<Label
							className='dataSourcePanelStaticCaption'
							label={StringResources.get('dataPreparationCaption')}
						/>
						<InlineLoading
							iconDescription={StringResources.get('preparingData')}
							description={this._getLoadProgressString()}
						/>
					</div>
				</FlexLayout>
			);
		}

		_renderTreeError(title, caption) {
			return (
				<DataSourcePanelStatic
					className='dataSourcePanelMetadataError'
					iconType={DataSourcePanelStatic.ICON_TYPES.error}
					title={title}
					caption={caption}
				/>
			);
		}

		_renderTreePlaceholder() {
			return (
				<div
					className='dataSourcePanelMetadataTreeContainer'
					ref={this.treeContainer.ref}
				/>
			);
		}

		_renderShapingTree(module, element) {
			return ShapingUIUtils.renderTree(
				module,
				this.props.dashboardApi,
				element,
				(payload, key) => this._onShapingTreeKeyPress(payload, key)
			);
		}

		_copySelectedTreeItems() {
			return this.props.dataSource.getModule().then(
				module => ShapingUIUtils.copySelectedTreeItems(module)
			);
		}

		_onShapingTreeKeyPress(payload, key) {
			if ((key.ctrlKey || key.metaKey) && key.keyCode === KeyCodes.C) {
				return this._copySelectedTreeItems();
			} else if (key.shiftKey && key.keyCode === KeyCodes.RIGHT_ARROW) {
				return this._insertionHelper.addToCanvas(payload);
			} else if (key.shiftKey && key.altKey && key.keyCode === KeyCodes.F) {
				return this._insertionHelper.addToGlobalFilter(payload);
			} else if (key.shiftKey && key.keyCode === KeyCodes.F) {
				return this._insertionHelper.addToLocalFilter(payload);
			}
		}
	}

	return DataSourcePanelMetadataTree;
});
